<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
        <!-- Calendar view could be added here in the future -->
      </div>
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="addInterval" v-if="activeTab === 'active'">{{ $t('add') }}</button>
      </div>
    </div>
    
    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'active' }" @click="switchTab('active')" href="#">
          {{ $t('active') }}
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'archived' }" @click="switchTab('archived')" href="#">
          {{ $t('archived') }}
        </a>
      </li>
    </ul>
    
    <!-- List View -->
    <div>
      <div class="actions row col-lg-12 mb-3 p-0 m-0 justify-content-start">
        <div class="actions-left">
          <div class="btn-group" role="group">
            <select class="form-control" v-model="experienceFilter" @change="onExperienceFilterChanged">
              <option value="">{{ $t('statistics.allExperiences') }}</option>
              <option v-for="experience in experiences" :key="experience.id" :value="experience.id">
                {{ experience.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('availabilityIntervals.intervals')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    <ConfirmationPopUp 
      :confirmationMessage="confirmationMessage" 
      @closeConfirmRequested="onClickConfirmationPopup"
      v-model="showConfirmationPopup"
    ></ConfirmationPopUp>
    <ConfirmDialog
      :title="confirmDialogTitle"
      :message="confirmDialogMessage"
      v-model="showConfirmDialog"
      @confirm="onConfirmDialog"
      @cancel="onCancelDialog"
    ></ConfirmDialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import ConfirmDialog from "@/controls/popups/ConfirmDialog.vue";
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, ConfirmationPopUp, ConfirmDialog },
  data () {
    return {
      activeTab: 'active',
      experienceFilter: '',
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      actions: [],
      sorterValue: { column: 'startDate', asc: true },
      loading: false,
      showConfirmationPopup: false,
      confirmationMessage: "",
      showConfirmDialog: false,
      confirmDialogTitle: "",
      confirmDialogMessage: "",
      pendingAction: null,
      pendingItem: null
    }
  },
  computed: {
    ...mapState('availabilityIntervals', ['getAvailabilityIntervalsOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState('availabilityIntervals', ['deleteAvailabilityIntervalByIdOutput']),
    ...mapState('availabilityIntervals', ['archiveAvailabilityIntervalByIdOutput']),
    ...mapState('availabilityIntervals', ['unarchiveAvailabilityIntervalByIdOutput']),
    experiences: function() {
      return (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
    },
    experienceIdToName() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      const map = new Map();
      list.forEach(e => {
        if (e && e.id != null) {
          map.set(e.id, e.name || '');
        }
      });
      return map;
    },
    availabilityIntervalsInfo: function() {
      return this.getAvailabilityIntervalsOutput ? this.getAvailabilityIntervalsOutput : null;
    },
    fields() {
      const locale = this.$i18n.locale;
      void locale;
      return [
        { key: 'experienceName', label: this.$t('availabilityIntervals.experience'), _classes: 'font-weight-bold', sortable: true },
        { key: 'guideName', label: this.$t('availabilityIntervals.guide'), sortable: true },
        { key: 'startDate', label: this.$t('availabilityIntervals.startDate'), sortable: true },
        { key: 'endDate', label: this.$t('availabilityIntervals.endDate'), sortable: true },
        { key: 'startTime', label: this.$t('availabilityIntervals.startTime'), sortable: true },
        { key: 'frequency', label: this.$t('availabilityIntervals.frequency'), sortable: true },
        { key: 'generatedSlotsCount', label: this.$t('availabilityIntervals.generatedSlots'), sortable: true },
        { key: 'isActive', label: this.$t('availabilityIntervals.isActive'), sortable: true },
      ];
    },
    items: function()
    {
      if(!(this.availabilityIntervalsInfo && this.availabilityIntervalsInfo.data)) return [];
      const currentLocale = this.$i18n.locale;
      return this.availabilityIntervalsInfo.data.map(x => ({
        ...x,
        experienceName: (x.experience && x.experience.name) ? x.experience.name : (this.experienceIdToName.get(x.experienceId) || `#${x.experienceId}`),
        guideName: x.guide && x.guide.name ? x.guide.name : '',
        startDate: x.startDate ? new Date(x.startDate).toLocaleDateString() : '',
        endDate: x.endDate ? new Date(x.endDate).toLocaleDateString() : '',
        startTime: x.startTime ? x.startTime.substring(0, 5) : '',
        frequency: this.getFrequencyLabel(x.frequency),
        isActive: x.isActive ? this.$t('availabilityIntervals.isActive') : this.$t('archived'),
        // Add dynamic actions based on isActive status
        // Edit button removed from UI but logic remains
        _actions: x.isActive 
          ? [
              { key: 'archive', label: this.$t('archive') }
            ]
          : [
              { key: 'activate', label: this.$t('activate') }
            ]
      }));
    },
    pages: function()
    {
      return this.availabilityIntervalsInfo ? this.availabilityIntervalsInfo.totalPages : 1;
    }
  },
  mounted: async function()
  {
    // Load experiences first for the filter dropdown - always use Portuguese to get all experiences with consistent names
    try {
      await this.getExperiences({ 
        input: {
          pageSize: 100, 
          pageNumber: 1,
          propertyName: 'Position',
          orderBy: 1
        },
        language: 'pt'
      });
    } catch (error) {
      console.error('Error loading experiences on mount:', error);
    }
    
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  activated: function()
  {
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('availabilityIntervals', ['getAvailabilityIntervals', 'deleteAvailabilityIntervalById', 'archiveAvailabilityIntervalById', 'unarchiveAvailabilityIntervalById']),
    ...mapActions('experiences', ['getExperiences']),
    getFrequencyLabel(frequency) {
      // Map string enum values to numbers if frequency comes as string from API
      const frequencyMap = {
        'Daily': 1,
        'Weekdays': 2,
        'Weekends': 3,
        'Monday': 4,
        'Tuesday': 5,
        'Wednesday': 6,
        'Thursday': 7,
        'Friday': 8,
        'Saturday': 9,
        'Sunday': 10
      };
      
      // Convert string to number if needed
      let frequencyNumber = frequency;
      if (typeof frequency === 'string' && frequencyMap[frequency] !== undefined) {
        frequencyNumber = frequencyMap[frequency];
      } else if (typeof frequency === 'string') {
        // Try to parse as number
        frequencyNumber = parseInt(frequency, 10);
      }
      
      // Use this.$i18n.t to ensure it uses the current locale context
      const labels = {
        1: this.$i18n.t('availabilityIntervals.frequencyDaily'),
        2: this.$i18n.t('availabilityIntervals.frequencyWeekdays'),
        3: this.$i18n.t('availabilityIntervals.frequencyWeekends'),
        4: this.$i18n.t('availabilityIntervals.frequencyMonday'),
        5: this.$i18n.t('availabilityIntervals.frequencyTuesday'),
        6: this.$i18n.t('availabilityIntervals.frequencyWednesday'),
        7: this.$i18n.t('availabilityIntervals.frequencyThursday'),
        8: this.$i18n.t('availabilityIntervals.frequencyFriday'),
        9: this.$i18n.t('availabilityIntervals.frequencySaturday'),
        10: this.$i18n.t('availabilityIntervals.frequencySunday')
      };
      return labels[frequencyNumber] || frequency;
    },
    rowClicked (item, index) {
      this.$router.push({ name: 'AvailabilityIntervalsEdit', query: { id: item.id } });
    },
    async actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.editInterval(item);
          break;
        }
        case 'archive':
        {
          this.pendingAction = 'archive';
          this.pendingItem = item;
          this.confirmDialogTitle = this.$t('archive');
          const intervalText = this.$i18n.locale === 'pt' ? 'intervalo de horários' : 'availability interval';
          this.confirmDialogMessage = this.$i18n.t('archive') + ' ' + intervalText + '?';
          this.showConfirmDialog = true;
          break;
        }
        case 'activate':
        {
          this.pendingAction = 'activate';
          this.pendingItem = item;
          this.confirmDialogTitle = this.$t('activate');
          const intervalText = this.$i18n.locale === 'pt' ? 'intervalo de horários' : 'availability interval';
          this.confirmDialogMessage = this.$i18n.t('activate') + ' ' + intervalText + '?';
          this.showConfirmDialog = true;
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, '', this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));
      
      // Add experience filter if selected
      if (this.experienceFilter) {
        input.experienceId = parseInt(this.experienceFilter);
      }
      
      // Add isActive filter based on active tab - always filter by status
      // Archived tab: show only inactive intervals (isActive = false)
      // Active tab: show only active intervals (isActive = true)
      // Always set the value explicitly to ensure it's sent to the API
      if (this.activeTab === 'archived') {
        input.isActive = false;
      } else {
        input.isActive = true;
      }
      
      if (!this.getExperiencesOutput || !this.getExperiencesOutput.data || this.getExperiencesOutput.data.length === 0) {
        this.getExperiences({ 
          input: {
            pageSize: 100, 
            pageNumber: 1,
            propertyName: 'Position',
            orderBy: 1
          },
          language: 'pt'
        });
      }

      this.getAvailabilityIntervals(input); 
    },
    switchTab(tab) {
      if (this.activeTab !== tab) {
        this.activeTab = tab;
        if (this.$refs.table) {
          this.$refs.table.reset();
        }
        // The activeTab watcher will handle the list update
      }
    },
    addInterval () 
    {
      router.push({ name: 'AvailabilityIntervalsAdd'});
    },
    editInterval(interval) {
      this.$router.push({ name: 'AvailabilityIntervalsEdit', query: { id: interval.id } });
    },
    onExperienceFilterChanged() {
      // Reset to page 1 when filter changes
      if (this.$refs.table) {
        this.$refs.table.reset();
      }
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    onClickConfirmationPopup(value) {
      this.showConfirmationPopup = value;
    },
    async onConfirmDialog() {
      this.showConfirmDialog = false;
      
      if (this.pendingItem) {
        this.loading = true; // Show loading immediately after confirmation
        
        try {
          if (this.pendingAction === 'archive') {
            await this.$store.dispatch('availabilityIntervals/archiveAvailabilityIntervalById', this.pendingItem.id);
            // Show success message
            this.$store.dispatch('alert/setAlert', {
              type: 'success',
              message: this.$i18n.locale === 'pt' 
                ? 'Intervalo de horários arquivado com sucesso' 
                : 'Availability interval archived successfully'
            });
          } else if (this.pendingAction === 'activate') {
            await this.$store.dispatch('availabilityIntervals/unarchiveAvailabilityIntervalById', this.pendingItem.id);
            // Show success message
            this.$store.dispatch('alert/setAlert', {
              type: 'success',
              message: this.$i18n.locale === 'pt' 
                ? 'Intervalo de horários ativado com sucesso' 
                : 'Availability interval activated successfully'
            });
          }
        } catch (error) {
          // Show error message if something goes wrong
          this.$store.dispatch('alert/setAlert', {
            type: 'error',
            message: this.$i18n.locale === 'pt' 
              ? 'Erro ao processar a operação' 
              : 'Error processing operation'
          });
        } finally {
          // Refresh the list after operation
          this.updateItemsRequested(1, this.itemsPerPage);
        }
      }
      
      this.pendingAction = null;
      this.pendingItem = null;
    },
    onCancelDialog() {
      this.showConfirmDialog = false;
      this.pendingAction = null;
      this.pendingItem = null;
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    deleteAvailabilityIntervalByIdOutput: function(val)
    {
      this.loading = true;

      if(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    archiveAvailabilityIntervalByIdOutput: function(val)
    {
      // Loading is already set in onConfirmDialog
      // This watcher will be triggered when the operation completes
      if(val)
      {
        // The loading will be cleared when getAvailabilityIntervalsOutput is updated
      }
    },
    unarchiveAvailabilityIntervalByIdOutput: function(val)
    {
      // Loading is already set in onConfirmDialog
      // This watcher will be triggered when the operation completes
      if(val)
      {
        // The loading will be cleared when getAvailabilityIntervalsOutput is updated
      }
    },
    getAvailabilityIntervalsOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    },
    activeTab: function(newTab, oldTab)
    {
      // When switching tabs, always refresh the list to ensure we have the latest data
      // The oldTab !== undefined check prevents refresh on initial mount
      if (newTab !== oldTab && oldTab !== undefined) {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    '$i18n.locale': function(newLocale, oldLocale)
    {
      // Force update when language changes to ensure actions column remains visible
      // The items computed will be recalculated automatically because it uses this.$t()
      // This watcher ensures the component reacts to locale changes
      if (newLocale !== oldLocale && oldLocale !== undefined) {
        this.$forceUpdate();
      }
    },
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
}

</style>

